import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  FaUserGraduate, 
  FaUserTie, 
  FaCalendarAlt, 
  FaEnvelope, 
  FaChevronDown, 
  FaChevronUp, 
  FaPaperPlane,
  FaEye,
  FaEyeSlash,
  FaSortAmountDown,
  FaFilter
} from 'react-icons/fa';

const AdminWritingTests = () => {
    const [pendingTests, setPendingTests] = useState([]);
    const [teachers, setTeachers] = useState([]);
    const [selectedTeacher, setSelectedTeacher] = useState('');
    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState('');
    const [filterStatus, setFilterStatus] = useState('pending');
    const [sortBy, setSortBy] = useState('newest');
    const [expandedCards, setExpandedCards] = useState({});
    const [showFullResponse, setShowFullResponse] = useState({});

    useEffect(() => {
        fetchData();
    }, [filterStatus]);

    const fetchData = async () => {
        try {
             let url = `${process.env.REACT_APP_BACKEND_URL}/api/auth/admin/writing-tasks/pending-review`;

            if (filterStatus !== 'all') {
                url += `?status=${filterStatus}`;
            }

            const [testsRes, teachersRes] = await Promise.all([
                axios.get(url),
                axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/teachers`)
            ]);

            // Sort tests
            let sortedTests = testsRes.data;
            if (sortBy === 'newest') {
                sortedTests.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
            } else if (sortBy === 'oldest') {
                sortedTests.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
            }

            setPendingTests(sortedTests);
            setTeachers(teachersRes.data);
        } catch (error) {
            console.error('Error fetching data:', error);
            setMessage('Error loading data');
        } finally {
            setLoading(false);
        }
    };

    const assignToTeacher = async (testId, studentId) => {
  if (!selectedTeacher) {
    setMessage('Please select a teacher first');
    return;
  }

  try {
    const user = JSON.parse(localStorage.getItem('user'));

    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/assign-writing-test`, {
      test_id: testId,
      student_id: studentId,
      teacher_id: selectedTeacher,
      assigned_by: user.id
    });

    setMessage('Test assigned successfully!');
    
    // Remove only the assigned test from the list
    setPendingTests(prev => prev.filter(test => test.id !== testId));
    
    setTimeout(() => setMessage(''), 3000);
  } catch (error) {
    console.error('Error assigning test:', error);
    setMessage('Error assigning test: ' + (error.response?.data?.message || error.message));
  }
};

    const parseWritingResponse = (responses) => {
        if (typeof responses === 'string') {
            try {
                const parsed = JSON.parse(responses);
                return parsed.writing || responses;
            } catch {
                return responses;
            }
        }
        return responses?.writing || 'No response';
    };

    const toggleCardExpansion = (testId) => {
        setExpandedCards(prev => ({
            ...prev,
            [testId]: !prev[testId]
        }));
    };

    const toggleFullResponse = (testId) => {
        setShowFullResponse(prev => ({
            ...prev,
            [testId]: !prev[testId]
        }));
    };

    const getStatusBadge = (test) => {
        if (test.teacher_assigned) {
            return <span className="px-3 py-1 text-xs font-semibold bg-yellow-100 text-yellow-800 rounded-full">Assigned</span>;
        }
        return <span className="px-3 py-1 text-xs font-semibold bg-blue-100 text-blue-800 rounded-full">Pending</span>;
    };

    const getWordCount = (text) => {
        return text ? text.trim().split(/\s+/).length : 0;
    };

    const truncateText = (text, maxLength = 200) => {
        if (!text || text.length <= maxLength) return text;
        return text.substring(0, maxLength) + '...';
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto"></div>
                    <p className="mt-4 text-gray-600 font-medium">Loading writing tests...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className="mb-8 text-center">
                    <h1 className="text-4xl font-bold text-gray-800 mb-2">Writing Test Management</h1>
                    <p className="text-gray-600 text-lg">Manage and assign student writing tests to teachers</p>
                </div>

                {/* Message Alert */}
                {message && (
                    <div className={`mb-6 p-4 rounded-lg shadow-md transition-all duration-300 ${
                        message.includes('success') 
                            ? 'bg-green-100 border border-green-300 text-green-800' 
                            : 'bg-red-100 border border-red-300 text-red-800'
                    }`}>
                        <div className="flex items-center justify-between">
                            <span className="font-semibold">{message}</span>
                            <button onClick={() => setMessage('')} className="text-lg font-bold">×</button>
                        </div>
                    </div>
                )}

                {/* Filters and Controls */}
                <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
                    <div className="flex flex-col lg:flex-row gap-4 items-start lg:items-center justify-between">
                        <div className="flex flex-col sm:flex-row gap-4 flex-1">
                            {/* Status Filter */}
                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                    <FaFilter className="mr-2 text-blue-500" />
                                    Filter by Status
                                </label>
                                <select
                                    value={filterStatus}
                                    onChange={(e) => setFilterStatus(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-3 bg-white shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                                >
                                    <option value="pending">Pending Review</option>
                                    <option value="assigned">Assigned to Teachers</option>
                                    <option value="all">All Writing Tests</option>
                                </select>
                            </div>

                            {/* Teacher Selection */}
                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                    <FaUserTie className="mr-2 text-green-500" />
                                    Assign to Teacher
                                </label>
                                <select
                                    value={selectedTeacher}
                                    onChange={(e) => setSelectedTeacher(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-3 bg-white shadow-sm focus:ring-2 focus:ring-green-500 focus:border-green-500 transition-all"
                                >
                                    <option value="">Select a teacher...</option>
                                    {teachers.map(teacher => (
                                        <option key={teacher.id} value={teacher.id}>
                                            {teacher.first_name} {teacher.last_name}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {/* Sort Options */}
                            <div className="flex-1">
                                <label className="block text-sm font-semibold text-gray-700 mb-2 flex items-center">
                                    <FaSortAmountDown className="mr-2 text-purple-500" />
                                    Sort by
                                </label>
                                <select
                                    value={sortBy}
                                    onChange={(e) => setSortBy(e.target.value)}
                                    className="w-full border border-gray-300 rounded-lg p-3 bg-white shadow-sm focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all"
                                >
                                    <option value="newest">Newest First</option>
                                    <option value="oldest">Oldest First</option>
                                </select>
                            </div>
                        </div>

                        {/* Refresh Button */}
                        <button
                            onClick={fetchData}
                            className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-3 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-300 transform hover:scale-105 mt-4 lg:mt-0"
                        >
                            Refresh
                        </button>
                    </div>
                </div>

                {/* Tests Grid */}
                {pendingTests.length === 0 ? (
                    <div className="bg-white rounded-xl shadow-lg p-12 text-center">
                        <div className="text-6xl mb-4">📝</div>
                        <h3 className="text-2xl font-bold text-gray-700 mb-2">No Writing Tests Found</h3>
                        <p className="text-gray-500 text-lg">
                            {filterStatus === 'pending' 
                                ? "All writing tests have been assigned to teachers!" 
                                : "No writing tests match your current filters."}
                        </p>
                    </div>
                ) : (
                    <div className="grid gap-6">
                        {pendingTests.map(test => (
                            <div key={test.id} className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100">
                                {/* Card Header */}
                                <div className="p-6 border-b border-gray-100">
                                    <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                                        <div className="flex items-start space-x-4">
                                            <div className="bg-blue-100 p-3 rounded-full">
                                                <FaUserGraduate className="text-2xl text-blue-600" />
                                            </div>
                                            <div>
                                                <div className="flex items-center space-x-3 mb-2">
                                                    <h3 className="text-xl font-bold text-gray-800">
                                                        {test.first_name} {test.last_name}
                                                    </h3>
                                                    {getStatusBadge(test)}
                                                </div>
                                                <div className="flex flex-wrap gap-4 text-sm text-gray-600">
                                                    <span className="flex items-center">
                                                        <FaUserGraduate className="mr-1" />
                                                        Grade {test.grade}
                                                    </span>
                                                    <span className="flex items-center">
                                                        <FaCalendarAlt className="mr-1" />
                                                        {new Date(test.created_at).toLocaleDateString('en-US', {
                                                            year: 'numeric',
                                                            month: 'short',
                                                            day: 'numeric',
                                                            hour: '2-digit',
                                                            minute: '2-digit'
                                                        })}
                                                    </span>
                                                    <span className="flex items-center">
                                                        <FaEnvelope className="mr-1" />
                                                        {test.parent_email}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        
                                        <div className="flex flex-col sm:flex-row gap-3">
                                            <button
                                                onClick={() => toggleCardExpansion(test.id)}
                                                className="flex items-center justify-center px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                                            >
                                                {expandedCards[test.id] ? (
                                                    <>
                                                        <FaEyeSlash className="mr-2" />
                                                        Collapse
                                                    </>
                                                ) : (
                                                    <>
                                                        <FaEye className="mr-2" />
                                                        Expand
                                                    </>
                                                )}
                                            </button>
                                            <button
                                                onClick={() => assignToTeacher(test.id, test.student_id)}
                                                disabled={!selectedTeacher}
                                                className={`flex items-center justify-center px-6 py-2 rounded-lg font-semibold transition-all duration-300 ${
                                                    selectedTeacher 
                                                        ? 'bg-green-500 hover:bg-green-600 text-white shadow-md hover:shadow-lg transform hover:scale-105' 
                                                        : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                                                }`}
                                            >
                                                <FaPaperPlane className="mr-2" />
                                                Assign
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Expandable Content */}
                                {expandedCards[test.id] && (
                                    <div className="p-6 space-y-6 animate-fadeIn">
                                        {/* Student & Parent Info */}
                                        <div className="grid md:grid-cols-2 gap-6">
                                            <div className="bg-blue-50 rounded-lg p-4">
                                                <h4 className="font-semibold text-blue-800 mb-3 flex items-center">
                                                    <FaUserGraduate className="mr-2" />
                                                    Student Information
                                                </h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>Name:</strong> {test.first_name} {test.last_name}</p>
                                                    <p><strong>Grade:</strong> {test.grade}</p>
                                                    <p><strong>Submitted:</strong> {new Date(test.created_at).toLocaleString()}</p>
                                                </div>
                                            </div>
                                            <div className="bg-green-50 rounded-lg p-4">
                                                <h4 className="font-semibold text-green-800 mb-3 flex items-center">
                                                    <FaUserTie className="mr-2" />
                                                    Parent Information
                                                </h4>
                                                <div className="space-y-2 text-sm">
                                                    <p><strong>Name:</strong> {test.parent_first_name} {test.parent_last_name}</p>
                                                    <p><strong>Email:</strong> {test.parent_email}</p>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Writing Response */}
                                        <div className="bg-gray-50 rounded-lg p-6">
                                            <div className="flex justify-between items-center mb-4">
                                                <h4 className="font-semibold text-gray-800 text-lg">Writing Response</h4>
                                                <div className="flex items-center space-x-4">
                                                    <span className="text-sm text-gray-500 bg-white px-3 py-1 rounded-full border">
                                                        {getWordCount(parseWritingResponse(test.responses))} words
                                                    </span>
                                                    <button
                                                        onClick={() => toggleFullResponse(test.id)}
                                                        className="text-blue-600 hover:text-blue-800 text-sm font-semibold flex items-center"
                                                    >
                                                        {showFullResponse[test.id] ? (
                                                            <>Show Less <FaChevronUp className="ml-1" /></>
                                                        ) : (
                                                            <>Show Full <FaChevronDown className="ml-1" /></>
                                                        )}
                                                    </button>
                                                </div>
                                            </div>
                                            <div className="bg-white rounded-lg p-4 border">
                                                <p className="text-gray-700 leading-relaxed whitespace-pre-wrap font-serif">
                                                    {showFullResponse[test.id] 
                                                        ? parseWritingResponse(test.responses)
                                                        : truncateText(parseWritingResponse(test.responses))
                                                    }
                                                </p>
                                            </div>
                                        </div>

                                        {/* AI Feedback */}
                                        {test.ai_feedback && (
                                            <div className="bg-indigo-50 rounded-lg p-6">
                                                <h4 className="font-semibold text-indigo-800 mb-4 flex items-center">
                                                    🤖 AI Preliminary Feedback
                                                </h4>
                                                <div className="bg-white rounded-lg p-4 border border-indigo-200">
                                                    <p className="text-gray-700 leading-relaxed whitespace-pre-wrap">
                                                        {test.ai_feedback}
                                                    </p>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Stats Footer */}
                <div className="mt-8 bg-white rounded-xl shadow-lg p-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
                        <div className="p-4">
                            <div className="text-3xl font-bold text-blue-600">{pendingTests.length}</div>
                            <div className="text-gray-600">Total Tests</div>
                        </div>
                        <div className="p-4">
                            <div className="text-3xl font-bold text-yellow-600">
                                {pendingTests.filter(test => test.teacher_assigned).length}
                            </div>
                            <div className="text-gray-600">Assigned</div>
                        </div>
                        <div className="p-4">
                            <div className="text-3xl font-bold text-green-600">
                                {pendingTests.filter(test => !test.teacher_assigned).length}
                            </div>
                            <div className="text-gray-600">Pending Assignment</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Custom CSS for animations */}
            <style jsx>{`
                @keyframes fadeIn {
                    from { opacity: 0; transform: translateY(-10px); }
                    to { opacity: 1; transform: translateY(0); }
                }
                .animate-fadeIn {
                    animation: fadeIn 0.3s ease-out;
                }
            `}</style>
        </div>
    );
};

export default AdminWritingTests;